import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Switch } from '../components/ui/switch';
import {
  ArrowLeft, Search, Loader2, Users, BookOpen, Trophy,
  Flame, Clock, ChevronRight, X, Zap, Eye, EyeOff,
  ToggleLeft, ToggleRight, Mail, Calendar, PauseCircle, PlayCircle,
  Plus, Trash2, Save, Edit, ShoppingBag, Upload, Copy, Star, Activity, Video, Music, ExternalLink, GripVertical, Image, KeyRound, MessageCircle, CheckCircle2
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;


const SECTIONS_LIST = [
  { key: 'daily_practice', label: 'Daily Practice' },
  { key: 'membership', label: 'Membership' },
  { key: 'rank_login', label: 'Rank & Login' },
  { key: 'journey', label: 'Journey & Progress' },
  { key: 'schedule', label: 'Schedule' },
  { key: 'qimen', label: 'Qimen Activation' },
  { key: 'ecosystem', label: 'Life Decision' },
  { key: 'training', label: 'Special Training' },
  { key: 'courses', label: 'My Courses' },
  { key: 'merchandise', label: 'Merchandise' },
];

const StudentSectionToggles = ({ student, onSaved }) => {
  const [localHidden, setLocalHidden] = useState(new Set(student.hidden_sections || []));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLocalHidden(new Set(student.hidden_sections || []));
  }, [student.user_id, student.hidden_sections]);

  const origHidden = new Set(student.hidden_sections || []);
  const hasChanges = origHidden.size !== localHidden.size || [...origHidden].some(k => !localHidden.has(k)) || [...localHidden].some(k => !origHidden.has(k));

  const toggleLocal = (key) => {
    setLocalHidden(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const saveAll = async () => {
    setSaving(true);
    for (const s of SECTIONS_LIST) {
      const shouldEnable = !localHidden.has(s.key);
      const wasEnabled = !origHidden.has(s.key);
      if (shouldEnable !== wasEnabled) {
        await fetch(`${BACKEND_URL}/api/admin/students/${student.user_id}/sections`, {
          method: 'PUT', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ key: s.key, enabled: shouldEnable }),
        });
      }
    }
    setSaving(false);
    onSaved();
  };

  return (
    <div data-testid="student-sections" className="mb-4">
      <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
        <ToggleLeft className="w-4 h-4 text-amber-400" /> Portal Sections
        <span className="text-xs text-gray-500 font-normal">— for this student only</span>
      </h3>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
        {SECTIONS_LIST.map(s => {
          const enabled = !localHidden.has(s.key);
          return (
            <div key={s.key} onClick={() => toggleLocal(s.key)}
              className={`flex items-center gap-2 px-2.5 py-1.5 rounded-md cursor-pointer transition-colors border ${
                enabled ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-[#1a1a1a] border-[#252525] opacity-50'
              }`} data-testid={`student-section-${s.key}`}>
              <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${enabled ? 'bg-emerald-400' : 'bg-red-400'}`} />
              <span className={`text-[11px] truncate ${enabled ? 'text-white' : 'text-gray-500'}`}>{s.label}</span>
            </div>
          );
        })}
      </div>
      {hasChanges && (
        <div className="flex justify-end mt-2">
          <Button size="sm" onClick={saveAll} disabled={saving}
            className="bg-emerald-600 hover:bg-emerald-500 h-7 text-xs" data-testid="save-sections-btn">
            {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />} Save Changes
          </Button>
        </div>
      )}
    </div>
  );
};



const EntitlementToggles = ({ student, entitlementEdits, setEntitlementEdits, togglingAccess, markEntitlement, onSaved }) => {
  const [localEnabled, setLocalEnabled] = useState(() => {
    const map = {};
    (student.entitlements || []).forEach(e => { map[e.key] = e.enabled !== false; });
    return map;
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const map = {};
    (student.entitlements || []).forEach(e => { map[e.key] = e.enabled !== false; });
    setLocalEnabled(map);
  }, [student.user_id, student.entitlements]);

  const origEnabled = {};
  (student.entitlements || []).forEach(e => { origEnabled[e.key] = e.enabled !== false; });

  const hasChanges = (student.entitlements || []).some(e => localEnabled[e.key] !== origEnabled[e.key]) ||
    (student.entitlements || []).some(e => {
      const editVal = parseInt(entitlementEdits[e.key]);
      return !isNaN(editVal) && editVal !== e.total;
    });

  const saveAll = async () => {
    setSaving(true);
    const updated = (student.entitlements || []).map(e => ({
      ...e,
      enabled: localEnabled[e.key] !== false,
      total: Math.max(0, parseInt(entitlementEdits[e.key]) || e.total),
    }));
    await fetch(`${BACKEND_URL}/api/admin/students/${student.user_id}/entitlements`, {
      method: 'PUT', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ entitlements: updated }),
    });
    setSaving(false);
    onSaved();
  };

  return (
    <>
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs text-gray-500 uppercase tracking-wider">Entitlements</p>
        {hasChanges && (
          <Button size="sm" onClick={saveAll} disabled={saving}
            className="bg-emerald-600 hover:bg-emerald-500 h-7 text-xs" data-testid="save-entitlements-btn">
            {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />} Save Changes
          </Button>
        )}
      </div>
      <div className="space-y-2.5">
        {(student.entitlements || []).map(ent => {
          const editTotal = entitlementEdits[ent.key] ?? ent.total;
          const remaining = editTotal - ent.used;
          const isEnabled = localEnabled[ent.key] !== false;
          const useLoading = togglingAccess[`ent-${ent.key}-use`];
          const undoLoading = togglingAccess[`ent-${ent.key}-undo`];
          return (
            <div key={ent.key} className={`flex items-center justify-between gap-3 rounded-lg p-3 ${isEnabled ? 'bg-[#1a1a1a]' : 'bg-[#1a1a1a] opacity-40'}`}
              data-testid={`entitlement-${ent.key}`}>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={isEnabled}
                    onCheckedChange={() => setLocalEnabled(prev => ({ ...prev, [ent.key]: !isEnabled }))}
                    className="scale-75"
                    data-testid={`toggle-ent-${ent.key}`}
                  />
                  <p className={`text-sm ${isEnabled && remaining > 0 ? 'text-white' : 'text-gray-500'}`}>{ent.label}</p>
                </div>
                {isEnabled && (
                  <div className="flex items-center gap-2 mt-1 ml-9">
                    <span className="text-[11px] text-gray-500">Assign:</span>
                    <input type="number" min="0" max="99"
                      value={editTotal}
                      onChange={e => setEntitlementEdits(prev => ({ ...prev, [ent.key]: e.target.value }))}
                      className="w-14 bg-gray-800 border border-gray-700 text-white text-center text-xs rounded px-1 py-0.5"
                      data-testid={`assign-${ent.key}`} />
                    <span className="text-[11px] text-gray-500">Used: {ent.used}</span>
                    {remaining > 0
                      ? <span className="text-[11px] text-emerald-400">{remaining} left</span>
                      : <span className="text-[11px] text-gray-600">all used</span>
                    }
                  </div>
                )}
              </div>
              {isEnabled && (
                <div className="flex items-center gap-1.5 shrink-0">
                  <button
                    onClick={() => markEntitlement(student.user_id, ent.key, 'undo')}
                    disabled={ent.used === 0 || undoLoading}
                    className="px-2 py-1 text-xs rounded bg-gray-800 text-gray-400 hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed"
                    data-testid={`undo-${ent.key}`}>
                    {undoLoading ? '...' : '- Undo'}
                  </button>
                  <button
                    onClick={() => markEntitlement(student.user_id, ent.key, 'use')}
                    disabled={remaining <= 0 || useLoading}
                    className="px-2 py-1 text-xs rounded bg-amber-600 text-white hover:bg-amber-500 disabled:opacity-30 disabled:cursor-not-allowed"
                    data-testid={`use-${ent.key}`}>
                    {useLoading ? '...' : '+ Mark Used'}
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
};


const StudentProgressManager = ({ user }) => {
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [togglingAccess, setTogglingAccess] = useState({});
  const [membershipForm, setMembershipForm] = useState({ start: '', duration: '1_year' });
  const [savingMembership, setSavingMembership] = useState(false);
  const [entitlementEdits, setEntitlementEdits] = useState({});
  const [savingEntitlements, setSavingEntitlements] = useState(false);
  const [qimenItems, setQimenItems] = useState([]);
  const [qimenForm, setQimenForm] = useState({ label: '', date: '', time: '' });
  const [editingQimen, setEditingQimen] = useState(null);
  const [savingQimen, setSavingQimen] = useState(false);
  const [merchItems, setMerchItems] = useState([]);
  const [merchForm, setMerchForm] = useState({ name: '', description: '', price: '', image_url: '', points_cost: '', discount_percent: '' });
  const [editingMerch, setEditingMerch] = useState(null);
  const [savingMerch, setSavingMerch] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [studentActivity, setStudentActivity] = useState(null);
  const [trainingVideos, setTrainingVideos] = useState([]);
  const [trainingForm, setTrainingForm] = useState({ title: '', video_url: '', description: '', category: 'special' });
  const [editingTraining, setEditingTraining] = useState(null);
  const [savingTraining, setSavingTraining] = useState(false);
  const [portalSections, setPortalSections] = useState([]);
  const [savingSections, setSavingSections] = useState(false);
  const [activePanel, setActivePanel] = useState('students');
  // Lifetunes state
  const [ltTracks, setLtTracks] = useState([]);
  const [ltForm, setLtForm] = useState({ title: '', subtitle: '', image_url: '', link_url: 'https://lifetunes.live', featured: false });
  const [ltShowForm, setLtShowForm] = useState(false);
  const [ltSaving, setLtSaving] = useState(null);
  const [ltUploading, setLtUploading] = useState(false);
  const [ltDragging, setLtDragging] = useState(false);

  const fetchStudents = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/students/progress`, { credentials: 'include' });
      if (res.ok) setStudents(await res.json());
    } catch (e) { console.error(e); }
  }, []);

  const fetchCourses = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/courses`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setCourses(data.courses || []);
      }
    } catch (e) { console.error(e); }
  }, []);

  const fetchQimen = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/qimen`, { credentials: 'include' });
      if (res.ok) setQimenItems(await res.json());
    } catch (e) { console.error(e); }
  }, []);

  const fetchMerch = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/merchandise`, { credentials: 'include' });
      if (res.ok) setMerchItems(await res.json());
    } catch (e) { console.error(e); }
  }, []);

  const fetchTraining = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/training-videos`, { credentials: 'include' });
      if (res.ok) setTrainingVideos(await res.json());
    } catch (e) { console.error(e); }
  }, []);

  const fetchPortalConfig = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/portal-config`, { credentials: 'include' });
      if (res.ok) { const d = await res.json(); setPortalSections(d.sections); }
    } catch (e) { console.error(e); }
  }, []);

  const fetchLtTracks = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/lifetunes/tracks`, { credentials: 'include' });
      if (res.ok) { const d = await res.json(); setLtTracks(d.tracks || []); }
    } catch (e) { console.error(e); }
  }, []);

  const ltUploadFile = async (file) => {
    if (!file) return;
    setLtUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${BACKEND_URL}/api/upload/image`, { method: 'POST', credentials: 'include', body: fd });
      if (res.ok) { const d = await res.json(); setLtForm(p => ({ ...p, image_url: d.url })); }
    } catch (e) { console.error(e); }
    finally { setLtUploading(false); }
  };

  const ltCreate = async () => {
    if (!ltForm.title) return;
    setLtSaving('new');
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/lifetunes/tracks`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...ltForm, order: ltTracks.length })
      });
      if (res.ok) { setLtForm({ title: '', subtitle: '', image_url: '', link_url: 'https://lifetunes.live', featured: false }); setLtShowForm(false); fetchLtTracks(); }
    } catch (e) { console.error(e); }
    finally { setLtSaving(null); }
  };

  const ltToggleFeatured = async (t) => {
    setLtSaving(t.track_id);
    try {
      await fetch(`${BACKEND_URL}/api/admin/lifetunes/tracks/${t.track_id}`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ featured: !t.featured })
      });
      fetchLtTracks();
    } catch (e) { console.error(e); }
    finally { setLtSaving(null); }
  };

  const ltDelete = async (id) => {
    if (!window.confirm('Delete this track?')) return;
    setLtSaving(id);
    try {
      await fetch(`${BACKEND_URL}/api/admin/lifetunes/tracks/${id}`, { method: 'DELETE', credentials: 'include' });
      fetchLtTracks();
    } catch (e) { console.error(e); }
    finally { setLtSaving(null); }
  };

  useEffect(() => {
    Promise.all([fetchStudents(), fetchCourses(), fetchQimen(), fetchMerch(), fetchTraining(), fetchPortalConfig(), fetchLtTracks()]).then(() => setLoading(false));
  }, [fetchStudents, fetchCourses]);

  const openStudentDetail = async (userId) => {
    setSelectedStudent(userId);
    setDetailLoading(true);
    setStudentActivity(null);
    try {
      const [detailRes, activityRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/admin/students/${userId}/detail`, { credentials: 'include' }),
        fetch(`${BACKEND_URL}/api/admin/students/${userId}/activity`, { credentials: 'include' }),
      ]);
      if (detailRes.ok) {
        const data = await detailRes.json();
        setDetailData(data);
        setMembershipForm({
          start: data.student.membership_start || '',
          duration: data.student.membership_duration || '1_year',
        });
        setWhatsappForm({ value: data.student.whatsapp || '', saving: false, editing: false, status: '', errorMsg: '' });
        const edits = {};
        (data.student.entitlements || []).forEach(e => { edits[e.key] = e.total; });
        setEntitlementEdits(edits);
      }
      if (activityRes.ok) setStudentActivity(await activityRes.json());
    } catch (e) { console.error(e); }
    finally { setDetailLoading(false); }
  };

  const toggleStudentCourseAccess = async (userId, courseId, grant) => {
    const key = `${userId}-${courseId}`;
    setTogglingAccess(prev => ({ ...prev, [key]: true }));
    try {
      await fetch(`${BACKEND_URL}/api/admin/students/${userId}/course-access`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ course_id: courseId, grant }),
      });
      await openStudentDetail(userId);
    } catch (e) { console.error(e); }
    finally { setTogglingAccess(prev => ({ ...prev, [key]: false })); }
  };

  const toggleGlobalCourseAccess = async (courseId) => {
    setTogglingAccess(prev => ({ ...prev, [courseId]: true }));
    try {
      await fetch(`${BACKEND_URL}/api/admin/courses/${courseId}/toggle-access`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      await fetchCourses();
    } catch (e) { console.error(e); }
    finally { setTogglingAccess(prev => ({ ...prev, [courseId]: false })); }
  };

  const togglePauseStudent = async (userId) => {
    setTogglingAccess(prev => ({ ...prev, [`pause-${userId}`]: true }));
    try {
      await fetch(`${BACKEND_URL}/api/admin/students/${userId}/pause`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      await openStudentDetail(userId);
      await fetchStudents();
    } catch (e) { console.error(e); }
    finally { setTogglingAccess(prev => ({ ...prev, [`pause-${userId}`]: false })); }
  };

  const saveMembership = async (userId) => {
    if (!membershipForm.start) return;
    setSavingMembership(true);
    try {
      const entitlements = detailData?.student?.entitlements?.map(e => ({
        ...e,
        total: Math.max(0, parseInt(entitlementEdits[e.key]) || 0),
      }));
      await fetch(`${BACKEND_URL}/api/admin/students/${userId}/membership`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          membership_start: membershipForm.start,
          membership_duration: membershipForm.duration,
          entitlements: entitlements && entitlements.length > 0 ? entitlements : undefined,
        }),
      });
      await openStudentDetail(userId);
      await fetchStudents();
    } catch (e) { console.error(e); }
    finally { setSavingMembership(false); }
  };

  const markEntitlement = async (userId, key, action) => {
    const toggleKey = `ent-${key}-${action}`;
    setTogglingAccess(prev => ({ ...prev, [toggleKey]: true }));
    try {
      await fetch(`${BACKEND_URL}/api/admin/students/${userId}/entitlement-use`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ key, action }),
      });
      await openStudentDetail(userId);
    } catch (e) { console.error(e); }
    finally { setTogglingAccess(prev => ({ ...prev, [toggleKey]: false })); }
  };

  // Admin: Reset student password (auto-sends via WATI WhatsApp; falls back to manual share)
  const [resetPwState, setResetPwState] = useState({ open: false, loading: false, tempPassword: '', studentEmail: '', studentName: '', studentPhone: '', whatsappSent: false, whatsappError: null, copied: false });
  const [whatsappForm, setWhatsappForm] = useState({ value: '', saving: false, editing: false, status: '', errorMsg: '' });

  const saveStudentWhatsapp = async () => {
    if (!detailData) return;
    const valueToSave = (whatsappForm.value || '').trim();
    setWhatsappForm(prev => ({ ...prev, saving: true, status: '', errorMsg: '' }));
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/students/${detailData.student.user_id}/whatsapp`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ whatsapp: valueToSave }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
      // Update local detailData with new whatsapp; switch back to view mode
      setDetailData(prev => prev ? { ...prev, student: { ...prev.student, whatsapp: data.whatsapp || '' } } : prev);
      setWhatsappForm({ value: data.whatsapp || '', saving: false, editing: false, status: 'saved', errorMsg: '' });
      setTimeout(() => {
        setWhatsappForm(prev => ({ ...prev, status: '' }));
      }, 2500);
    } catch (e) {
      console.error('Save WhatsApp failed:', e);
      setWhatsappForm(prev => ({ ...prev, saving: false, status: 'error', errorMsg: e.message || 'Save failed' }));
    }
  };

  const resetStudentPassword = async () => {
    if (!detailData) return;
    setResetPwState(prev => ({ ...prev, loading: true }));
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/students/${detailData.student.user_id}/reset-password`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ send_whatsapp: true }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to reset password');
      setResetPwState({
        open: true,
        loading: false,
        tempPassword: data.temp_password,
        studentEmail: data.student_email || detailData.student.email,
        studentName: data.student_name || detailData.student.name,
        studentPhone: data.whatsapp_number || detailData.student.whatsapp || '',
        whatsappSent: !!data.whatsapp_sent,
        whatsappError: data.whatsapp_error,
        copied: false,
      });
    } catch (e) {
      alert('Reset failed: ' + e.message);
      setResetPwState(prev => ({ ...prev, loading: false }));
    }
  };

  const buildWhatsAppLink = () => {
    const { tempPassword, studentName, studentPhone } = resetPwState;
    const firstName = (studentName || 'there').split(' ')[0];
    const msg = `Hi ${firstName}, your Life Decoder password has been reset.\n\nTemporary password: ${tempPassword}\n\nPlease log in at https://lifedecoder.com/login and change it from your Profile settings.\n\n— Master Steven, Life Decoder`;
    const phone = (studentPhone || '').replace(/[^0-9]/g, '');
    return phone
      ? `https://wa.me/${phone}?text=${encodeURIComponent(msg)}`
      : `https://wa.me/?text=${encodeURIComponent(msg)}`;
  };

  const copyTempPassword = () => {
    navigator.clipboard.writeText(resetPwState.tempPassword);
    setResetPwState(prev => ({ ...prev, copied: true }));
    setTimeout(() => setResetPwState(prev => ({ ...prev, copied: false })), 1500);
  };

  const saveEntitlementTotals = async (userId) => {
    if (!detailData) return;
    setSavingEntitlements(true);
    try {
      const updated = detailData.student.entitlements.map(e => ({
        ...e,
        total: Math.max(0, parseInt(entitlementEdits[e.key]) || 0),
      }));
      await fetch(`${BACKEND_URL}/api/admin/students/${userId}/entitlements`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ entitlements: updated }),
      });
      await openStudentDetail(userId);
    } catch (e) { console.error(e); }
    finally { setSavingEntitlements(false); }
  };

  const saveQimen = async () => {
    if (!qimenForm.label || !qimenForm.date) return;
    setSavingQimen(true);
    try {
      const url = editingQimen
        ? `${BACKEND_URL}/api/admin/qimen/${editingQimen}`
        : `${BACKEND_URL}/api/admin/qimen`;
      const method = editingQimen ? 'PUT' : 'POST';
      await fetch(url, {
        method, credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(qimenForm),
      });
      await fetchQimen();
      setQimenForm({ label: '', date: '', time: '' });
      setEditingQimen(null);
    } catch (e) { console.error(e); }
    finally { setSavingQimen(false); }
  };

  const deleteQimen = async (itemId) => {
    if (!window.confirm('Delete this activation?')) return;
    await fetch(`${BACKEND_URL}/api/admin/qimen/${itemId}`, { method: 'DELETE', credentials: 'include' });
    await fetchQimen();
  };

  const saveMerch = async () => {
    if (!merchForm.name || !merchForm.price) return;
    setSavingMerch(true);
    try {
      const url = editingMerch
        ? `${BACKEND_URL}/api/admin/merchandise/${editingMerch}`
        : `${BACKEND_URL}/api/admin/merchandise`;
      const method = editingMerch ? 'PUT' : 'POST';
      await fetch(url, {
        method, credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...merchForm,
          points_cost: parseInt(merchForm.points_cost) || 0,
          discount_percent: parseInt(merchForm.discount_percent) || 0,
        }),
      });
      await fetchMerch();
      setMerchForm({ name: '', description: '', price: '', image_url: '', points_cost: '', discount_percent: '' });
      setEditingMerch(null);
    } catch (e) { console.error(e); }
    finally { setSavingMerch(false); }
  };

  const deleteMerch = async (itemId) => {
    if (!window.confirm('Delete this item?')) return;
    await fetch(`${BACKEND_URL}/api/admin/merchandise/${itemId}`, { method: 'DELETE', credentials: 'include' });
    await fetchMerch();
  };

  const duplicateMerch = async (item) => {
    await fetch(`${BACKEND_URL}/api/admin/merchandise`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: `${item.name} (Copy)`,
        description: item.description || '',
        price: item.price,
        image_url: item.image_url || '',
        points_cost: item.points_cost || 0,
        discount_percent: item.discount_percent || 0,
      }),
    });
    await fetchMerch();
  };

  const toggleFeatured = async (item) => {
    const currentFeaturedCount = merchItems.filter(m => m.featured).length;
    if (!item.featured && currentFeaturedCount >= 3) {
      alert('Maximum 3 featured items allowed. Unfeature one first.');
      return;
    }
    await fetch(`${BACKEND_URL}/api/admin/merchandise/${item.item_id}`, {
      method: 'PUT', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ featured: !item.featured }),
    });
    await fetchMerch();
  };

  const saveTraining = async () => {
    if (!trainingForm.title || !trainingForm.video_url) return;
    setSavingTraining(true);
    try {
      const url = editingTraining
        ? `${BACKEND_URL}/api/admin/training-videos/${editingTraining}`
        : `${BACKEND_URL}/api/admin/training-videos`;
      await fetch(url, {
        method: editingTraining ? 'PUT' : 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(trainingForm),
      });
      await fetchTraining();
      setTrainingForm({ title: '', video_url: '', description: '', category: 'special' });
      setEditingTraining(null);
    } catch (e) { console.error(e); }
    finally { setSavingTraining(false); }
  };

  const deleteTraining = async (id) => {
    if (!window.confirm('Delete this training video?')) return;
    await fetch(`${BACKEND_URL}/api/admin/training-videos/${id}`, { method: 'DELETE', credentials: 'include' });
    await fetchTraining();
  };


  const toggleMerchAvailable = async (item) => {
    await fetch(`${BACKEND_URL}/api/admin/merchandise/${item.item_id}`, {
      method: 'PUT', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ available: !item.available }),
    });
    await fetchMerch();
  };

  const handleImageUpload = async (file) => {
    if (!file || !file.type.startsWith('image/')) return;
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`${BACKEND_URL}/api/upload/image`, {
        method: 'POST', credentials: 'include', body: formData,
      });
      if (res.ok) {
        const data = await res.json();
        setMerchForm(p => ({ ...p, image_url: data.url }));
      }
    } catch (e) { console.error(e); }
    finally { setUploadingImage(false); }
  };


  const toggleSection = async (key) => {
    const updated = portalSections.map(s => s.key === key ? { ...s, enabled: !s.enabled } : s);
    setPortalSections(updated);
    setSavingSections(true);
    try {
      await fetch(`${BACKEND_URL}/api/admin/portal-config`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sections: updated }),
      });
    } catch (e) { console.error(e); }
    finally { setSavingSections(false); }
  };

  const filtered = students.filter(s =>
    s.name.toLowerCase().includes(search.toLowerCase()) ||
    s.email.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 pt-20 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950 pt-20">
      {/* Reset Password Modal */}
      {resetPwState.open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          onClick={() => setResetPwState(prev => ({ ...prev, open: false }))}
          data-testid="reset-password-modal"
        >
          <div
            className="bg-[#141414] border border-amber-500/30 rounded-xl shadow-2xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                  <h3 className="text-lg font-semibold text-white">Password Reset</h3>
                </div>
                <p className="text-sm text-gray-400">
                  for <span className="text-white">{resetPwState.studentName}</span>
                </p>
                <p className="text-xs text-gray-500">{resetPwState.studentEmail}</p>
              </div>
              <button
                onClick={() => setResetPwState(prev => ({ ...prev, open: false }))}
                className="text-gray-500 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="bg-[#1a1a1a] border border-gray-800 rounded-lg p-4 mb-4">
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">Temporary Password</p>
              <div className="flex items-center gap-2">
                <code className="text-xl font-mono font-bold text-amber-400 tracking-wider flex-1" data-testid="temp-password-value">
                  {resetPwState.tempPassword}
                </code>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={copyTempPassword}
                  className="border-gray-700 text-gray-300 hover:bg-gray-800"
                  data-testid="copy-temp-password-btn"
                >
                  {resetPwState.copied
                    ? <><CheckCircle2 className="w-4 h-4 mr-1 text-emerald-400" /> Copied</>
                    : <><Copy className="w-4 h-4 mr-1" /> Copy</>}
                </Button>
              </div>
              <p className="text-[11px] text-gray-500 mt-3 leading-relaxed">
                The student's existing sessions have been logged out. They must use this password to log in, then change it from their Profile.
              </p>
            </div>

            {/* WhatsApp delivery status */}
            {resetPwState.whatsappSent ? (
              <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4 mb-3" data-testid="whatsapp-sent-status">
                <div className="flex items-center gap-2 text-emerald-400">
                  <CheckCircle2 className="w-5 h-5" />
                  <span className="font-medium">WhatsApp sent automatically</span>
                </div>
                <p className="text-xs text-gray-400 mt-1">
                  Delivered via WATI to {resetPwState.studentPhone}
                </p>
              </div>
            ) : resetPwState.studentPhone ? (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mb-3" data-testid="whatsapp-failed-status">
                <div className="flex items-center gap-2 text-red-400 mb-1">
                  <X className="w-5 h-5" />
                  <span className="font-medium">WATI auto-send failed</span>
                </div>
                <p className="text-xs text-gray-400">
                  Number {resetPwState.studentPhone} may not be on WhatsApp. Send manually below.
                </p>
              </div>
            ) : (
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 mb-3">
                <p className="text-xs text-amber-200">
                  No WhatsApp number on file. Add one in the student detail to auto-send next time.
                </p>
              </div>
            )}

            <a
              href={buildWhatsAppLink()}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 w-full bg-[#25D366] hover:bg-[#1faa55] text-white font-medium py-3 rounded-lg transition-colors"
              data-testid="send-whatsapp-btn"
            >
              <MessageCircle className="w-5 h-5" />
              {resetPwState.whatsappSent ? 'Send Again Manually' : 'Send via WhatsApp Manually'}
            </a>

            <button
              onClick={() => setResetPwState(prev => ({ ...prev, open: false }))}
              className="block w-full text-sm text-gray-400 hover:text-white mt-3 py-1"
              data-testid="close-reset-modal-btn"
            >
              Done
            </button>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" onClick={() => navigate('/admin')} className="text-gray-400 hover:text-white" data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-white">Student Control Panel</h1>
            <p className="text-gray-500 text-sm">Monitor progress and control course visibility</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 border-b border-gray-800 pb-0">
          <button onClick={() => setActivePanel('students')}
            className={`px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${activePanel === 'students' ? 'text-amber-400 border-amber-400' : 'text-gray-500 border-transparent hover:text-gray-300'}`}
            data-testid="tab-students">
            <Users className="w-4 h-4 inline mr-1.5 -mt-0.5" />Students
          </button>
          <button onClick={() => setActivePanel('lifetunes')}
            className={`px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${activePanel === 'lifetunes' ? 'text-teal-400 border-teal-400' : 'text-gray-500 border-transparent hover:text-gray-300'}`}
            data-testid="tab-lifetunes">
            <Music className="w-4 h-4 inline mr-1.5 -mt-0.5" />Lifetunes
          </button>
        </div>

        {/* ===== LIFETUNES PANEL ===== */}
        {activePanel === 'lifetunes' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <p className="text-gray-400 text-sm">Manage tracks that appear in the Student Portal</p>
              <Button onClick={() => setLtShowForm(!ltShowForm)} className="bg-teal-600 hover:bg-teal-500" data-testid="add-track-btn">
                <Plus className="w-4 h-4 mr-1" /> Add Track
              </Button>
            </div>

            {ltShowForm && (
              <Card className="bg-gray-900 border-gray-800 mb-6" data-testid="add-track-form">
                <CardContent className="p-6 space-y-4">
                  <h3 className="text-white font-semibold">New Track</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs text-gray-400 mb-1 block">Title *</label>
                      <Input value={ltForm.title} onChange={e => setLtForm(p => ({ ...p, title: e.target.value }))}
                        placeholder="e.g. Morning Clarity" className="bg-gray-800 border-gray-700 text-white" data-testid="track-title-input" />
                    </div>
                    <div>
                      <label className="text-xs text-gray-400 mb-1 block">Subtitle / Description</label>
                      <Input value={ltForm.subtitle} onChange={e => setLtForm(p => ({ ...p, subtitle: e.target.value }))}
                        placeholder="e.g. 432 Hz - Natural Harmony" className="bg-gray-800 border-gray-700 text-white" data-testid="track-subtitle-input" />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-gray-400 mb-1 block">Track Link URL *</label>
                    <Input value={ltForm.link_url} onChange={e => setLtForm(p => ({ ...p, link_url: e.target.value }))}
                      placeholder="https://lifetunes.live/track/..." className="bg-gray-800 border-gray-700 text-white" data-testid="track-link-input" />
                  </div>
                  <div>
                    <label className="text-xs text-gray-400 mb-1 block">Cover Image</label>
                    <div
                      onDrop={(e) => { e.preventDefault(); setLtDragging(false); const f = e.dataTransfer.files?.[0]; if (f?.type.startsWith('image/')) ltUploadFile(f); }}
                      onDragOver={(e) => { e.preventDefault(); setLtDragging(true); }}
                      onDragLeave={() => setLtDragging(false)}
                      onClick={() => document.getElementById('lt-img-input').click()}
                      className={`relative rounded-xl border-2 border-dashed p-6 text-center cursor-pointer transition-all ${
                        ltDragging ? 'border-teal-400 bg-teal-500/10' : ltForm.image_url ? 'border-gray-700 bg-gray-800/30' : 'border-gray-700 bg-gray-800/30 hover:border-gray-600'
                      }`}
                      data-testid="track-image-dropzone">
                      <input id="lt-img-input" type="file" accept="image/*" className="hidden"
                        onChange={(e) => ltUploadFile(e.target.files?.[0])} />
                      {ltUploading ? (
                        <div className="flex flex-col items-center gap-2 py-2">
                          <Loader2 className="w-8 h-8 text-teal-400 animate-spin" />
                          <p className="text-sm text-teal-400">Uploading...</p>
                        </div>
                      ) : ltForm.image_url ? (
                        <div className="flex items-center gap-4">
                          <img src={ltForm.image_url} alt="cover" className="w-20 h-20 rounded-lg object-cover" />
                          <div className="text-left flex-1">
                            <p className="text-sm text-white font-medium">Image uploaded</p>
                            <p className="text-xs text-gray-500 mt-1">Drop a new image to replace</p>
                          </div>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center gap-2 py-2">
                          <Upload className="w-8 h-8 text-gray-500" />
                          <p className="text-sm text-gray-400">Drag & drop image here</p>
                          <p className="text-xs text-gray-600">or click to browse</p>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <input type="checkbox" checked={ltForm.featured} onChange={e => setLtForm(p => ({ ...p, featured: e.target.checked }))}
                      className="w-4 h-4 rounded border-gray-600 bg-gray-800 text-teal-500" data-testid="track-featured-checkbox" />
                    <span className="text-sm text-gray-300">Featured track (shown prominently at top)</span>
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={ltCreate} disabled={!ltForm.title || ltSaving === 'new'} className="bg-teal-600 hover:bg-teal-500" data-testid="save-track-btn">
                      {ltSaving === 'new' ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />} Save Track
                    </Button>
                    <Button variant="outline" onClick={() => setLtShowForm(false)} className="border-gray-700 text-gray-300">Cancel</Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {ltTracks.length === 0 ? (
              <Card className="bg-gray-900 border-gray-800">
                <CardContent className="p-12 text-center">
                  <Music className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                  <p className="text-gray-400 mb-2">No tracks yet</p>
                  <p className="text-gray-600 text-sm">Add tracks that will appear in the Student Portal Lifetunes section</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-2">
                {ltTracks.map((track) => (
                  <div key={track.track_id}
                    className="flex items-center gap-3 p-3 bg-gray-900 border border-gray-800 rounded-xl hover:border-gray-700 transition-colors"
                    data-testid={`track-item-${track.track_id}`}>
                    <GripVertical className="w-4 h-4 text-gray-600 shrink-0" />
                    {track.image_url ? (
                      <img src={track.image_url} alt={track.title} className="w-12 h-12 rounded-lg object-cover shrink-0" />
                    ) : (
                      <div className="w-12 h-12 rounded-lg bg-teal-500/10 flex items-center justify-center shrink-0">
                        <Music className="w-5 h-5 text-teal-400" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-medium text-sm truncate">{track.title}</p>
                      {track.subtitle && <p className="text-gray-500 text-xs truncate">{track.subtitle}</p>}
                      <a href={track.link_url} target="_blank" rel="noopener noreferrer"
                        className="text-teal-400 text-xs hover:underline flex items-center gap-1 mt-0.5">
                        {track.link_url?.substring(0, 40)}{track.link_url?.length > 40 ? '...' : ''} <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                    <button onClick={() => ltToggleFeatured(track)}
                      className={`p-2 rounded-lg transition-colors ${track.featured ? 'bg-amber-500/10 text-amber-400' : 'text-gray-600 hover:text-gray-400'}`}
                      data-testid={`toggle-featured-${track.track_id}`}>
                      <Star className={`w-4 h-4 ${track.featured ? 'fill-amber-400' : ''}`} />
                    </button>
                    <button onClick={() => ltDelete(track.track_id)}
                      className="p-2 rounded-lg text-gray-600 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                      data-testid={`delete-track-${track.track_id}`}>
                      {ltSaving === track.track_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ===== STUDENTS PANEL ===== */}
        {activePanel === 'students' && (<>
        {/* Portal Section Visibility */}
        <Card className="bg-[#141414] border-gray-800 mb-6" data-testid="portal-sections-config">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-white font-semibold text-sm flex items-center gap-2">
                <ToggleLeft className="w-4 h-4 text-amber-400" /> Portal Sections Visibility
              </h3>
              <span className="text-[10px] text-gray-500">Toggle sections on/off for all students</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
              {portalSections.map(s => (
                <div key={s.key}
                  onClick={() => toggleSection(s.key)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-colors border ${
                    s.enabled
                      ? 'bg-emerald-500/10 border-emerald-500/30'
                      : 'bg-[#1a1a1a] border-[#252525] opacity-50'
                  }`}
                  data-testid={`section-toggle-${s.key}`}>
                  <div className={`w-2 h-2 rounded-full shrink-0 ${s.enabled ? 'bg-emerald-400' : 'bg-red-400'}`} />
                  <span className={`text-xs truncate ${s.enabled ? 'text-white' : 'text-gray-500'}`}>{s.label}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* LEFT: Student List */}
          <div className="lg:col-span-1">
            <div className="mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <Input value={search} onChange={e => setSearch(e.target.value)}
                  placeholder="Search students..." className="pl-9 bg-gray-800 border-gray-700 text-white"
                  data-testid="search-students" />
              </div>
            </div>

            <p className="text-xs text-gray-500 mb-2">{filtered.length} students</p>

            <div className="space-y-2 max-h-[calc(100vh-260px)] overflow-y-auto pr-1">
              {filtered.map(s => (
                <Card key={s.user_id}
                  className={`cursor-pointer transition-all ${
                    selectedStudent === s.user_id
                      ? 'bg-amber-500/10 border-amber-500/40'
                      : 'bg-[#141414] border-gray-800 hover:border-gray-700'
                  }`}
                  onClick={() => openStudentDetail(s.user_id)}
                  data-testid={`student-card-${s.user_id}`}>
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <p className="text-white font-medium text-sm truncate">{s.name}</p>
                          {s.account_paused && <Badge className="bg-red-500/20 text-red-400 border-red-500/30 text-[10px] px-1.5 py-0">Paused</Badge>}
                        </div>
                        <p className="text-xs text-gray-500 truncate">{s.email}</p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-gray-600 shrink-0" />
                    </div>
                    <div className="flex items-center gap-3 mt-2 text-[11px]">
                      <span className="flex items-center gap-1 text-gray-500">
                        <BookOpen className="w-3 h-3" /> {s.enrolled_courses}
                      </span>
                      <span className="flex items-center gap-1 text-amber-500">
                        <Trophy className="w-3 h-3" /> {s.total_points}
                      </span>
                      <span className="flex items-center gap-1 text-orange-400">
                        <Flame className="w-3 h-3" /> {s.login_streak}d
                      </span>
                      {s.membership_duration && (
                        <span className="flex items-center gap-1 text-sky-400">
                          <Calendar className="w-3 h-3" /> {s.membership_duration === 'masterminder' ? 'MM' : s.membership_duration === '2_year' ? '2Y' : s.membership_duration === 'extended' ? 'EXT' : '1Y'}
                        </span>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
              {filtered.length === 0 && (
                <p className="text-gray-600 text-sm text-center py-8">No students found</p>
              )}
            </div>
          </div>

          {/* RIGHT: Detail Panel */}
          <div className="lg:col-span-2">
            {!selectedStudent ? (
              <Card className="bg-[#141414] border-gray-800">
                <CardContent className="p-12 text-center">
                  <Users className="w-12 h-12 text-gray-700 mx-auto mb-3" />
                  <p className="text-gray-500">Select a student to view progress and manage access</p>
                </CardContent>
              </Card>
            ) : detailLoading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
              </div>
            ) : detailData ? (
              <div className="space-y-6">
                {/* Student Info Header */}
                <Card className="bg-[#141414] border-gray-800" data-testid="student-detail-header">
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-3">
                          <h2 className="text-xl font-semibold text-white">{detailData.student.name}</h2>
                          {detailData.student.account_paused && (
                            <Badge className="bg-red-500/20 text-red-400 border-red-500/30">Paused</Badge>
                          )}
                        </div>
                        <p className="text-sm text-gray-500 flex items-center gap-1 mt-0.5">
                          <Mail className="w-3 h-3" /> {detailData.student.email}
                        </p>
                        {/* WhatsApp number — View / Edit modes */}
                        <div className="mt-2" data-testid="whatsapp-editor">
                          <div className="flex items-center gap-2 flex-wrap">
                            <MessageCircle className="w-3 h-3 text-[#25D366] flex-shrink-0" />

                            {whatsappForm.editing ? (
                              <>
                                <Input
                                  value={whatsappForm.value}
                                  onChange={(e) => setWhatsappForm(prev => ({ ...prev, value: e.target.value, status: '', errorMsg: '' }))}
                                  placeholder="+60123456789 (with country code)"
                                  autoFocus
                                  className="h-7 text-xs bg-[#1a1a1a] border-gray-800 text-white placeholder:text-gray-600 w-[220px]"
                                  data-testid="whatsapp-input"
                                />
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={saveStudentWhatsapp}
                                  disabled={whatsappForm.saving}
                                  className="h-7 px-3 text-xs border-emerald-600/50 text-emerald-300 hover:bg-emerald-600/10 flex-shrink-0"
                                  data-testid="save-whatsapp-btn"
                                >
                                  {whatsappForm.saving ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Save'}
                                </Button>
                                <button
                                  type="button"
                                  onClick={() => setWhatsappForm({ value: detailData.student.whatsapp || '', saving: false, editing: false, status: '', errorMsg: '' })}
                                  className="text-[11px] text-gray-500 hover:text-gray-300 flex-shrink-0"
                                  data-testid="cancel-whatsapp-btn"
                                >
                                  Cancel
                                </button>
                              </>
                            ) : (
                              <>
                                {detailData.student.whatsapp ? (
                                  <span className="text-sm text-gray-300 font-mono" data-testid="whatsapp-display">
                                    +{detailData.student.whatsapp}
                                  </span>
                                ) : (
                                  <span className="text-xs text-gray-500 italic" data-testid="whatsapp-display-empty">
                                    Not set
                                  </span>
                                )}
                                <button
                                  type="button"
                                  onClick={() => setWhatsappForm(prev => ({ ...prev, editing: true, value: detailData.student.whatsapp ? `+${detailData.student.whatsapp}` : '', status: '' }))}
                                  className="text-[11px] text-emerald-400 hover:text-emerald-300 underline-offset-2 hover:underline flex-shrink-0"
                                  data-testid="edit-whatsapp-btn"
                                >
                                  {detailData.student.whatsapp ? 'Edit' : 'Add'}
                                </button>
                                {whatsappForm.status === 'saved' && (
                                  <span className="flex items-center gap-1 text-[11px] text-emerald-400 flex-shrink-0" data-testid="whatsapp-saved-status">
                                    <CheckCircle2 className="w-3 h-3" /> Saved
                                  </span>
                                )}
                              </>
                            )}
                          </div>
                          {whatsappForm.status === 'error' && whatsappForm.editing && (
                            <p className="text-[11px] text-red-400 mt-1 ml-5" data-testid="whatsapp-error-status">
                              {whatsappForm.errorMsg || 'Save failed'}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => window.open(`/portal/preview/${detailData.student.user_id}`, '_blank')}
                          className="border-sky-500/40 text-sky-400 hover:bg-sky-500/10"
                          data-testid="view-portal-btn"
                        >
                          <Eye className="w-4 h-4 mr-1.5" /> View Portal
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={resetStudentPassword}
                          disabled={resetPwState.loading}
                          className="border-amber-500/40 text-amber-400 hover:bg-amber-500/10"
                          data-testid="reset-password-btn"
                        >
                          {resetPwState.loading
                            ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                            : <KeyRound className="w-4 h-4 mr-1.5" />}
                          Reset Password
                        </Button>
                        {togglingAccess[`pause-${detailData.student.user_id}`] ? (
                          <Loader2 className="w-4 h-4 text-gray-500 animate-spin" />
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => togglePauseStudent(detailData.student.user_id)}
                            className={detailData.student.account_paused
                              ? 'border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10'
                              : 'border-red-500/40 text-red-400 hover:bg-red-500/10'
                            }
                            data-testid="toggle-pause-btn"
                          >
                            {detailData.student.account_paused
                              ? <><PlayCircle className="w-4 h-4 mr-1.5" /> Resume Account</>
                              : <><PauseCircle className="w-4 h-4 mr-1.5" /> Pause Account</>
                            }
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" onClick={() => { setSelectedStudent(null); setDetailData(null); }}
                          className="text-gray-500 hover:text-white">
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
                      <div className="bg-[#1a1a1a] rounded-lg p-3 text-center">
                        <Trophy className="w-4 h-4 mx-auto mb-1 text-amber-400" />
                        <p className="text-lg font-bold text-white">{detailData.student.total_points}</p>
                        <p className="text-[10px] text-gray-500 uppercase">Points</p>
                      </div>
                      <div className="bg-[#1a1a1a] rounded-lg p-3 text-center">
                        <Flame className="w-4 h-4 mx-auto mb-1 text-orange-400" />
                        <p className="text-lg font-bold text-white">{detailData.student.login_streak}</p>
                        <p className="text-[10px] text-gray-500 uppercase">Streak</p>
                      </div>
                      <div className="bg-[#1a1a1a] rounded-lg p-3 text-center">
                        <Clock className="w-4 h-4 mx-auto mb-1 text-blue-400" />
                        <p className="text-lg font-bold text-white">{detailData.student.login_count}</p>
                        <p className="text-[10px] text-gray-500 uppercase">Logins</p>
                      </div>
                      <div className="bg-[#1a1a1a] rounded-lg p-3 text-center">
                        <BookOpen className="w-4 h-4 mx-auto mb-1 text-emerald-400" />
                        <p className="text-lg font-bold text-white">{detailData.enrolled_courses.length}</p>
                        <p className="text-[10px] text-gray-500 uppercase">Enrolled</p>
                      </div>
                    </div>
                    {/* Login Activity */}
                    <div className="mt-4">
                      <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">Login Activity (7 Days)</p>
                      <div className="flex gap-1.5">
                        {detailData.login_activity.map(day => (
                          <div key={day.date} className="flex-1 text-center">
                            <div className={`aspect-square rounded flex items-center justify-center ${
                              day.logged_in ? 'bg-emerald-500/20 border border-emerald-500/40' : 'bg-[#1a1a1a] border border-[#252525]'
                            }`}>
                              {day.logged_in && <Zap className="w-3 h-3 text-emerald-400" />}
                            </div>
                            <p className="text-[9px] text-gray-600 mt-0.5">
                              {new Date(day.date + 'T00:00:00').toLocaleDateString('en', { weekday: 'narrow' })}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Membership / Batch Info */}
                <Card className="bg-[#141414] border-gray-800" data-testid="membership-section">
                  <CardContent className="p-5">
                    <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-sky-400" /> Membership / Batch
                    </h3>

                    {detailData.student.membership_start ? (
                      <div className="bg-[#1a1a1a] rounded-lg p-4 mb-4">
                        <div className="grid grid-cols-3 gap-4 text-center">
                          <div>
                            <p className="text-[10px] text-gray-500 uppercase mb-1">Start Date</p>
                            <p className="text-sm font-medium text-white">
                              {new Date(detailData.student.membership_start + 'T00:00:00').toLocaleDateString('en', { day: 'numeric', month: 'short', year: 'numeric' })}
                            </p>
                          </div>
                          <div>
                            <p className="text-[10px] text-gray-500 uppercase mb-1">Duration</p>
                            <p className="text-sm font-medium text-sky-400">
                              {detailData.student.membership_duration === 'masterminder' ? 'Masterminder' : detailData.student.membership_duration === '2_year' ? '2 Years' : detailData.student.membership_duration === 'extended' ? 'Extended' : '1 Year'}
                            </p>
                          </div>
                          <div>
                            <p className="text-[10px] text-gray-500 uppercase mb-1">Expires</p>
                            <p className={`text-sm font-medium ${new Date(detailData.student.membership_end) < new Date() ? 'text-red-400' : 'text-emerald-400'}`}>
                              {new Date(detailData.student.membership_end + 'T00:00:00').toLocaleDateString('en', { day: 'numeric', month: 'short', year: 'numeric' })}
                            </p>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <p className="text-gray-500 text-sm mb-4">No membership set yet</p>
                    )}

                    <div className="flex items-end gap-3">
                      <div className="flex-1">
                        <label className="text-xs text-gray-400 block mb-1">Batch Start Date</label>
                        <Input type="date" value={membershipForm.start}
                          onChange={e => setMembershipForm(prev => ({ ...prev, start: e.target.value }))}
                          className="bg-gray-800 border-gray-700 text-white"
                          data-testid="membership-start-date" />
                      </div>
                      <div className="w-40">
                        <label className="text-xs text-gray-400 block mb-1">Duration</label>
                        <select value={membershipForm.duration}
                          onChange={e => setMembershipForm(prev => ({ ...prev, duration: e.target.value }))}
                          className="w-full bg-gray-800 border border-gray-700 text-white rounded-md px-3 py-2 text-sm"
                          data-testid="membership-duration">
                          <option value="1_year">1 Year</option>
                          <option value="2_year">2 Years</option>
                          <option value="extended">Extended</option>
                          <option value="masterminder">Masterminder</option>
                        </select>
                      </div>
                      <Button onClick={() => saveMembership(detailData.student.user_id)}
                        disabled={savingMembership || !membershipForm.start}
                        className="bg-sky-600 hover:bg-sky-500 shrink-0"
                        data-testid="save-membership-btn">
                        {savingMembership ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
                      </Button>
                    </div>

                    {detailData.student.entitlements && detailData.student.entitlements.length > 0 && (
                      <div className="mt-5 pt-5 border-t border-[#252525]" data-testid="entitlements-section">
                        <EntitlementToggles
                          student={detailData.student}
                          entitlementEdits={entitlementEdits}
                          setEntitlementEdits={setEntitlementEdits}
                          togglingAccess={togglingAccess}
                          markEntitlement={markEntitlement}
                          onSaved={() => openStudentDetail(detailData.student.user_id)}
                        />
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Per-Student Section Visibility */}
                <StudentSectionToggles
                  student={detailData.student}
                  onSaved={() => openStudentDetail(detailData.student.user_id)}
                />

                {/* Enrolled Courses with Access Toggle */}
                <div data-testid="enrolled-courses-section">
                  <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                    <BookOpen className="w-4 h-4 text-amber-400" /> Enrolled Courses
                    <span className="text-xs text-gray-500 font-normal">— toggle access on/off</span>
                  </h3>
                  {detailData.enrolled_courses.length === 0 ? (
                    <Card className="bg-[#141414] border-gray-800">
                      <CardContent className="p-6 text-center">
                        <p className="text-gray-500 text-sm">This student has no enrollments</p>
                      </CardContent>
                    </Card>
                  ) : (
                    <div className="space-y-2">
                      {detailData.enrolled_courses.map(c => {
                        const key = `${detailData.student.user_id}-${c.course_id}`;
                        const toggling = togglingAccess[key];
                        return (
                          <Card key={c.enrollment_id}
                            className={`border transition-colors ${c.access_revoked ? 'bg-red-950/20 border-red-500/20' : 'bg-[#141414] border-gray-800'}`}
                            data-testid={`enrollment-${c.enrollment_id}`}>
                            <CardContent className="p-4 flex items-center gap-4">
                              <div className="w-16 h-11 rounded overflow-hidden shrink-0 bg-[#1a1a1a]">
                                {c.thumbnail && <img src={c.thumbnail} alt="" className="w-full h-full object-cover" />}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <p className={`font-medium text-sm truncate ${c.access_revoked ? 'text-gray-500 line-through' : 'text-white'}`}>
                                    {c.title}
                                  </p>
                                  {c.access_revoked && (
                                    <Badge className="bg-red-500/20 text-red-400 border-red-500/30 text-[10px]">Revoked</Badge>
                                  )}
                                </div>
                                <div className="flex items-center gap-3 mt-1">
                                  <Progress value={c.progress} className="h-1.5 flex-1 bg-[#252525] max-w-[200px]" />
                                  <span className="text-xs text-amber-400 shrink-0">{Math.round(c.progress)}%</span>
                                  <span className="text-[11px] text-gray-500">{c.completed_lessons}/{c.total_lessons} lessons</span>
                                </div>
                              </div>
                              <div className="shrink-0 flex items-center gap-2">
                                {toggling ? (
                                  <Loader2 className="w-4 h-4 text-gray-500 animate-spin" />
                                ) : (
                                  <button
                                    onClick={(e) => { e.stopPropagation(); toggleStudentCourseAccess(detailData.student.user_id, c.course_id, c.access_revoked); }}
                                    className={`p-1.5 rounded-md transition-colors ${c.access_revoked ? 'text-red-400 hover:bg-red-500/10' : 'text-emerald-400 hover:bg-emerald-500/10'}`}
                                    title={c.access_revoked ? 'Restore access' : 'Revoke access'}
                                    data-testid={`toggle-access-${c.course_id}`}>
                                    {c.access_revoked ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                  </button>
                                )}
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Global Course Access */}
                <div data-testid="student-course-access">
                  <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                    <ToggleLeft className="w-4 h-4 text-amber-400" /> Course Access
                    <span className="text-xs text-gray-500 font-normal">— individual for this student</span>
                  </h3>
                  <Card className="bg-[#141414] border-gray-800">
                    <CardContent className="p-4">
                      <div className="space-y-3">
                        {courses.map(c => {
                          const enrollment = detailData.enrolled_courses.find(e => e.course_id === c.id);
                          const hasAccess = enrollment && !enrollment.access_revoked;
                          const key = `${detailData.student.user_id}-${c.id}`;
                          return (
                            <div key={c.id} className="flex items-center justify-between py-2 border-b border-[#1a1a1a] last:border-0"
                              data-testid={`course-access-${c.id}`}>
                              <div className="flex items-center gap-3 min-w-0">
                                <div className={`w-2 h-2 rounded-full shrink-0 ${hasAccess ? 'bg-emerald-400' : 'bg-red-400'}`} />
                                <p className={`text-sm truncate ${hasAccess ? 'text-white' : 'text-gray-500'}`}>{c.title}</p>
                                {!enrollment && <Badge className="bg-gray-700 text-gray-400 text-[10px] border-gray-600">Not enrolled</Badge>}
                              </div>
                              <div className="flex items-center gap-2 shrink-0">
                                <span className={`text-[11px] ${hasAccess ? 'text-emerald-400' : 'text-red-400'}`}>
                                  {hasAccess ? 'Access On' : 'Access Off'}
                                </span>
                                {togglingAccess[key] ? (
                                  <Loader2 className="w-4 h-4 text-gray-500 animate-spin" />
                                ) : (
                                  <Switch
                                    checked={!!hasAccess}
                                    onCheckedChange={() => toggleStudentCourseAccess(detailData.student.user_id, c.id, !hasAccess)}
                                    data-testid={`access-toggle-${c.id}`}
                                  />
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Activity Journey */}
                {studentActivity && studentActivity.activities.length > 0 && (
                  <div data-testid="student-activity-section">
                    <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                      <Activity className="w-4 h-4 text-emerald-400" /> Activity Journey
                      <span className="text-xs text-gray-500 font-normal">— {studentActivity.stats.total_activities} total</span>
                    </h3>
                    <Card className="bg-[#141414] border-gray-800">
                      <CardContent className="p-4 max-h-80 overflow-y-auto">
                        <div className="space-y-0">
                          {studentActivity.activities.slice(0, 30).map((a, i) => {
                            const icons = {
                              login: '🔑', streak: '🔥', mood_checkin: '💗', goal_set: '🎯',
                              commitment_set: '⚡', ecosystem_visit: '🌐', merch_redeem: '🛍',
                            };
                            const timeStr = new Date(a.created_at).toLocaleString('en', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
                            return (
                              <div key={i} className="flex gap-3 items-start">
                                <div className="flex flex-col items-center">
                                  <div className="w-7 h-7 rounded-full bg-[#1a1a1a] border border-[#252525] flex items-center justify-center text-xs">
                                    {icons[a.action] || '📌'}
                                  </div>
                                  {i < Math.min(studentActivity.activities.length, 30) - 1 && <div className="w-px h-5 bg-[#252525]" />}
                                </div>
                                <div className="flex-1 min-w-0 pb-1">
                                  <div className="flex items-center justify-between gap-2">
                                    <p className="text-sm text-white truncate">{a.description}</p>
                                    {a.points !== 0 && (
                                      <span className={`text-xs font-semibold shrink-0 ${a.points > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                        {a.points > 0 ? '+' : ''}{a.points}
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-[10px] text-gray-600">{timeStr}</p>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                )}
              </div>
            ) : null}
          </div>
        </div>

        {/* ─── Qimen Activation Dates (Global) ─── */}
        <div className="mt-8" data-testid="qimen-section">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <Calendar className="w-5 h-5 text-purple-400" /> Qimen Activation Date & Time
            </h2>
            <span className="text-xs text-gray-500">Visible to all students</span>
          </div>

          {/* Add/Edit Form */}
          <Card className="bg-[#141414] border-gray-800 mb-4">
            <CardContent className="p-4">
              <div className="flex items-end gap-3">
                <div className="flex-1">
                  <label className="text-xs text-gray-400 block mb-1">Label</label>
                  <Input value={qimenForm.label} onChange={e => setQimenForm(p => ({ ...p, label: e.target.value }))}
                    placeholder="e.g. Wealth Activation" className="bg-gray-800 border-gray-700 text-white"
                    data-testid="qimen-label" />
                </div>
                <div className="w-40">
                  <label className="text-xs text-gray-400 block mb-1">Date</label>
                  <Input type="date" value={qimenForm.date} onChange={e => setQimenForm(p => ({ ...p, date: e.target.value }))}
                    className="bg-gray-800 border-gray-700 text-white" data-testid="qimen-date" />
                </div>
                <div className="w-32">
                  <label className="text-xs text-gray-400 block mb-1">Time</label>
                  <Input value={qimenForm.time} onChange={e => setQimenForm(p => ({ ...p, time: e.target.value }))}
                    placeholder="e.g. 8:00 PM" className="bg-gray-800 border-gray-700 text-white"
                    data-testid="qimen-time" />
                </div>
                <Button onClick={saveQimen} disabled={savingQimen || !qimenForm.label || !qimenForm.date}
                  className="bg-purple-600 hover:bg-purple-500 shrink-0" data-testid="save-qimen-btn">
                  {savingQimen ? <Loader2 className="w-4 h-4 animate-spin" /> : editingQimen ? <Save className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                </Button>
                {editingQimen && (
                  <Button variant="ghost" size="sm" onClick={() => { setEditingQimen(null); setQimenForm({ label: '', date: '', time: '' }); }}
                    className="text-gray-400 shrink-0"><X className="w-4 h-4" /></Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* List */}
          {qimenItems.length === 0 ? (
            <p className="text-gray-600 text-sm text-center py-4">No activations added yet</p>
          ) : (
            <div className="space-y-2">
              {qimenItems.map(item => (
                <Card key={item.item_id} className="bg-[#141414] border-gray-800" data-testid={`qimen-${item.item_id}`}>
                  <CardContent className="p-3 flex items-center gap-3">
                    <div className="w-11 h-11 rounded-lg bg-purple-500/15 border border-purple-500/30 flex flex-col items-center justify-center shrink-0">
                      <span className="text-[10px] text-purple-400 uppercase leading-none">
                        {item.date ? new Date(item.date + 'T00:00:00').toLocaleDateString('en', { month: 'short' }) : ''}
                      </span>
                      <span className="text-sm font-bold text-purple-300 leading-none">
                        {item.date ? new Date(item.date + 'T00:00:00').getDate() : ''}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-medium text-sm truncate">{item.label}</p>
                      <p className="text-xs text-gray-500">
                        {item.date ? new Date(item.date + 'T00:00:00').toLocaleDateString('en', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) : ''}
                        {item.time && ` — ${item.time}`}
                      </p>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <button onClick={() => { setEditingQimen(item.item_id); setQimenForm({ label: item.label, date: item.date, time: item.time || '' }); }}
                        className="p-1.5 text-gray-500 hover:text-amber-400" data-testid={`edit-qimen-${item.item_id}`}>
                        <Edit className="w-4 h-4" />
                      </button>
                      <button onClick={() => deleteQimen(item.item_id)}
                        className="p-1.5 text-gray-500 hover:text-red-400" data-testid={`delete-qimen-${item.item_id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* ─── Merchandise (Global) ─── */}
        <div className="mt-8" data-testid="merch-section">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <ShoppingBag className="w-5 h-5 text-amber-400" /> Merchandise
            </h2>
            <span className="text-xs text-gray-500">
              {merchItems.filter(m => m.featured).length}/3 featured · Visible to all students
            </span>
          </div>

          <Card className="bg-[#141414] border-gray-800 mb-4">
            <CardContent className="p-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                <div>
                  <label className="text-xs text-gray-400 block mb-1">Product Name *</label>
                  <Input value={merchForm.name} onChange={e => setMerchForm(p => ({ ...p, name: e.target.value }))}
                    placeholder="e.g. Life Decoder T-Shirt" className="bg-gray-800 border-gray-700 text-white"
                    data-testid="merch-name" />
                </div>
                <div>
                  <label className="text-xs text-gray-400 block mb-1">Price *</label>
                  <Input value={merchForm.price} onChange={e => setMerchForm(p => ({ ...p, price: e.target.value }))}
                    placeholder="e.g. RM 89" className="bg-gray-800 border-gray-700 text-white"
                    data-testid="merch-price" />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs text-gray-400 block mb-1">Image</label>
                  <div
                    onDrop={e => { e.preventDefault(); handleImageUpload(e.dataTransfer.files[0]); }}
                    onDragOver={e => e.preventDefault()}
                    className="border-2 border-dashed border-gray-700 hover:border-amber-500/50 rounded-lg p-3 text-center cursor-pointer transition-colors"
                    onClick={() => document.getElementById('merch-file-input').click()}
                    data-testid="merch-dropzone"
                  >
                    <input id="merch-file-input" type="file" accept="image/*" className="hidden"
                      onChange={e => handleImageUpload(e.target.files[0])} />
                    {uploadingImage ? (
                      <div className="flex items-center justify-center gap-2 py-2">
                        <Loader2 className="w-4 h-4 text-amber-400 animate-spin" />
                        <span className="text-sm text-gray-400">Uploading...</span>
                      </div>
                    ) : merchForm.image_url ? (
                      <div className="flex items-center gap-3">
                        <img src={merchForm.image_url.startsWith('/api') ? `${BACKEND_URL}${merchForm.image_url}` : merchForm.image_url}
                          alt="" className="w-16 h-16 object-cover rounded" />
                        <div className="text-left flex-1 min-w-0">
                          <p className="text-xs text-emerald-400">Image uploaded</p>
                          <p className="text-[10px] text-gray-500 truncate">{merchForm.image_url}</p>
                        </div>
                        <button onClick={e => { e.stopPropagation(); setMerchForm(p => ({ ...p, image_url: '' })); }}
                          className="text-gray-500 hover:text-red-400 p-1"><X className="w-4 h-4" /></button>
                      </div>
                    ) : (
                      <div className="py-2">
                        <Upload className="w-6 h-6 text-gray-600 mx-auto mb-1" />
                        <p className="text-xs text-gray-500">Drag & drop image or click to browse</p>
                      </div>
                    )}
                  </div>
                </div>
                <div>
                  <label className="text-xs text-gray-400 block mb-1">Description</label>
                  <Input value={merchForm.description} onChange={e => setMerchForm(p => ({ ...p, description: e.target.value }))}
                    placeholder="Short description" className="bg-gray-800 border-gray-700 text-white"
                    data-testid="merch-desc" />
                </div>
                <div>
                  <label className="text-xs text-gray-400 block mb-1">Points Cost</label>
                  <Input type="number" value={merchForm.points_cost} onChange={e => setMerchForm(p => ({ ...p, points_cost: e.target.value }))}
                    placeholder="e.g. 2000" className="bg-gray-800 border-gray-700 text-white"
                    data-testid="merch-points" />
                </div>
                <div>
                  <label className="text-xs text-gray-400 block mb-1">Discount %</label>
                  <Input type="number" value={merchForm.discount_percent} onChange={e => setMerchForm(p => ({ ...p, discount_percent: e.target.value }))}
                    placeholder="e.g. 20" className="bg-gray-800 border-gray-700 text-white"
                    data-testid="merch-discount" />
                </div>
              </div>
              <div className="flex gap-2">
                <Button onClick={saveMerch} disabled={savingMerch || !merchForm.name || !merchForm.price}
                  className="bg-amber-600 hover:bg-amber-500" data-testid="save-merch-btn">
                  {savingMerch ? <Loader2 className="w-4 h-4 animate-spin" /> : editingMerch ? <><Save className="w-4 h-4 mr-1" /> Update</> : <><Plus className="w-4 h-4 mr-1" /> Add</>}
                </Button>
                {editingMerch && (
                  <Button variant="ghost" onClick={() => { setEditingMerch(null); setMerchForm({ name: '', description: '', price: '', image_url: '' }); }}
                    className="text-gray-400">Cancel</Button>
                )}
              </div>
            </CardContent>
          </Card>

          {merchItems.length === 0 ? (
            <p className="text-gray-600 text-sm text-center py-4">No merchandise added yet</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {merchItems.map(item => (
                <Card key={item.item_id} className={`border ${item.available ? 'bg-[#141414] border-gray-800' : 'bg-red-950/10 border-red-500/20'}`}
                  data-testid={`merch-${item.item_id}`}>
                  <CardContent className="p-3 flex gap-3">
                    {item.image_url && (
                      <div className="w-16 h-16 rounded-lg overflow-hidden shrink-0 bg-[#1a1a1a]">
                        <img src={item.image_url.startsWith('/api') ? `${BACKEND_URL}${item.image_url}` : item.image_url} alt="" className="w-full h-full object-cover" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className={`font-medium text-sm truncate ${item.available ? 'text-white' : 'text-gray-500 line-through'}`}>{item.name}</p>
                        <Badge className="bg-amber-500/20 text-amber-400 text-[10px] border-amber-500/30">{item.price}</Badge>
                        {item.featured && <Badge className="bg-yellow-500/20 text-yellow-400 text-[10px] border-yellow-500/30">Featured</Badge>}
                      </div>
                      {item.description && <p className="text-xs text-gray-500 truncate mt-0.5">{item.description}</p>}
                      {item.points_cost > 0 && (
                        <p className="text-[11px] text-purple-400 mt-0.5">{item.points_cost} pts = {item.discount_percent}% off</p>
                      )}
                    </div>
                    <div className="flex flex-col gap-1 shrink-0">
                      <button onClick={() => toggleFeatured(item)}
                        className={`p-1 rounded ${item.featured ? 'text-yellow-400 hover:bg-yellow-500/10' : 'text-gray-600 hover:text-yellow-400 hover:bg-yellow-500/10'}`}
                        title={item.featured ? 'Unfeature' : 'Feature'} data-testid={`star-merch-${item.item_id}`}>
                        <Star className={`w-4 h-4 ${item.featured ? 'fill-yellow-400' : ''}`} />
                      </button>
                      <button onClick={() => toggleMerchAvailable(item)}
                        className={`p-1 rounded ${item.available ? 'text-emerald-400 hover:bg-emerald-500/10' : 'text-red-400 hover:bg-red-500/10'}`}
                        title={item.available ? 'Hide' : 'Show'} data-testid={`toggle-merch-${item.item_id}`}>
                        {item.available ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                      </button>
                      <button onClick={() => { setEditingMerch(item.item_id); setMerchForm({ name: item.name, description: item.description || '', price: item.price, image_url: item.image_url || '', points_cost: item.points_cost || '', discount_percent: item.discount_percent || '' }); }}
                        className="p-1 text-gray-500 hover:text-amber-400" data-testid={`edit-merch-${item.item_id}`}>
                        <Edit className="w-4 h-4" />
                      </button>
                      <button onClick={() => duplicateMerch(item)}
                        className="p-1 text-gray-500 hover:text-sky-400" title="Duplicate" data-testid={`dup-merch-${item.item_id}`}>
                        <Copy className="w-4 h-4" />
                      </button>
                      <button onClick={() => deleteMerch(item.item_id)}
                        className="p-1 text-gray-500 hover:text-red-400" data-testid={`delete-merch-${item.item_id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* ─── Training Videos (Global) ─── */}
        <div className="mt-8" data-testid="training-section">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <Video className="w-5 h-5 text-purple-400" /> Training Videos
            </h2>
            <span className="text-xs text-gray-500">Visible to all students</span>
          </div>

          <Card className="bg-[#141414] border-gray-800 mb-4">
            <CardContent className="p-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                <div>
                  <label className="text-xs text-gray-400 block mb-1">Title *</label>
                  <Input value={trainingForm.title} onChange={e => setTrainingForm(p => ({ ...p, title: e.target.value }))}
                    placeholder="e.g. BaZi Basics Masterclass" className="bg-gray-800 border-gray-700 text-white"
                    data-testid="training-title" />
                </div>
                <div>
                  <label className="text-xs text-gray-400 block mb-1">Video URL *</label>
                  <Input value={trainingForm.video_url} onChange={e => setTrainingForm(p => ({ ...p, video_url: e.target.value }))}
                    placeholder="YouTube or Vimeo URL" className="bg-gray-800 border-gray-700 text-white"
                    data-testid="training-url" />
                </div>
                <div>
                  <label className="text-xs text-gray-400 block mb-1">Category</label>
                  <select value={trainingForm.category} onChange={e => setTrainingForm(p => ({ ...p, category: e.target.value }))}
                    className="w-full bg-gray-800 border border-gray-700 text-white rounded-md px-3 py-2 text-sm"
                    data-testid="training-category">
                    <option value="special">Special Training</option>
                    <option value="arise">Arise Training</option>
                    <option value="fengshui">FengshuiPlotter.live Training</option>
                    <option value="qmdj">QMDJ.live Training</option>
                    <option value="bazi">Bazi.live Training</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-gray-400 block mb-1">Description</label>
                  <Input value={trainingForm.description} onChange={e => setTrainingForm(p => ({ ...p, description: e.target.value }))}
                    placeholder="Short description" className="bg-gray-800 border-gray-700 text-white"
                    data-testid="training-desc" />
                </div>
              </div>
              <div className="flex gap-2">
                <Button onClick={saveTraining} disabled={savingTraining || !trainingForm.title || !trainingForm.video_url}
                  className="bg-purple-600 hover:bg-purple-500" data-testid="save-training-btn">
                  {savingTraining ? <Loader2 className="w-4 h-4 animate-spin" /> : editingTraining ? <><Save className="w-4 h-4 mr-1" /> Update</> : <><Plus className="w-4 h-4 mr-1" /> Add</>}
                </Button>
                {editingTraining && (
                  <Button variant="ghost" onClick={() => { setEditingTraining(null); setTrainingForm({ title: '', video_url: '', description: '', category: 'special' }); }}
                    className="text-gray-400">Cancel</Button>
                )}
              </div>
            </CardContent>
          </Card>

          {trainingVideos.length === 0 ? (
            <p className="text-gray-600 text-sm text-center py-4">No training videos added yet</p>
          ) : (
            <div className="space-y-2">
              {trainingVideos.map(v => {
                const catLabels = { special: 'Special', arise: 'Arise', fengshui: 'FengshuiPlotter', qmdj: 'QMDJ', bazi: 'Bazi' };
                return (
                  <Card key={v.video_id} className="bg-[#141414] border-gray-800" data-testid={`training-${v.video_id}`}>
                    <CardContent className="p-3 flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-purple-500/15 border border-purple-500/30 flex items-center justify-center shrink-0">
                        <Video className="w-4 h-4 text-purple-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-white font-medium text-sm truncate">{v.title}</p>
                          <Badge className="bg-purple-500/20 text-purple-400 text-[10px] border-purple-500/30">{catLabels[v.category] || v.category}</Badge>
                        </div>
                        {v.description && <p className="text-xs text-gray-500 truncate">{v.description}</p>}
                      </div>
                      <div className="flex gap-1 shrink-0">
                        <button onClick={() => { setEditingTraining(v.video_id); setTrainingForm({ title: v.title, video_url: v.video_url, description: v.description || '', category: v.category }); }}
                          className="p-1 text-gray-500 hover:text-amber-400"><Edit className="w-4 h-4" /></button>
                        <button onClick={() => deleteTraining(v.video_id)}
                          className="p-1 text-gray-500 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
        </>)}
      </div>
    </div>
  );
};

export default StudentProgressManager;
